import { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { apiService } from '../services/api.service';

const DELINQUENCY_TYPES = [
  { value: 'EMPRESA', label: 'Empresa' },
  { value: 'SUC', label: 'Sucursal' },
  { value: 'SEMESTRAL_EM', label: 'Semestral empresa' },
  { value: 'SEMESTRAL_SUC', label: 'Semestral sucursal' },
];

const CHART_COLORS = ['#0f172a', '#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#be185d'];

type ChartSeries = { key: string; label: string; color?: string };

type ChartConfig = {
  type: 'bar' | 'pie';
  labelKey: string;
  series: ChartSeries[];
};

type ReportDef = {
  key: string;
  title: string;
  description: string;
  group: string;
  needsDates?: boolean;
  needsYear?: boolean;
  needsDelinquencyType?: boolean;
  statusOptions?: { value: string; label: string }[];
  userFilter?: boolean;
  chart?: ChartConfig;
};

const REPORT_GROUPS = [
  'Ventas — Saldos',
  'Mora',
  'Estadísticas',
  'Cobranza',
  'Caja y seguros',
  'CRM y solicitudes',
] as const;

const LEGACY_REPORTS: ReportDef[] = [
  {
    key: 'sales-by-branch',
    title: 'Ventas por sucursal',
    description: 'Préstamos desembolsados por sucursal',
    group: 'Ventas — Saldos',
    needsDates: true,
    chart: {
      type: 'bar',
      labelKey: 'branchName',
      series: [
        { key: 'totalAmount', label: 'Monto', color: '#0f172a' },
        { key: 'loanCount', label: 'Cantidad', color: '#2563eb' },
      ],
    },
  },
  {
    key: 'sales-avg-by-branch',
    title: 'Ventas promedio por sucursal',
    description: 'Ticket promedio por vendedor dentro de cada sucursal',
    group: 'Ventas — Saldos',
    needsDates: true,
    chart: {
      type: 'bar',
      labelKey: 'label',
      series: [{ key: 'avgAmount', label: 'Promedio', color: '#059669' }],
    },
  },
  {
    key: 'sales-by-plan',
    title: 'Ventas por plan',
    description: 'Desembolsos agrupados por producto',
    group: 'Ventas — Saldos',
    needsDates: true,
    chart: {
      type: 'bar',
      labelKey: 'planName',
      series: [
        { key: 'totalAmount', label: 'Monto', color: '#0f172a' },
        { key: 'count', label: 'Cantidad', color: '#2563eb' },
      ],
    },
  },
  {
    key: 'sales-by-discount-code',
    title: 'Ventas por código descuento',
    description: 'Ventas con código promocional',
    group: 'Ventas — Saldos',
    needsDates: true,
    chart: {
      type: 'pie',
      labelKey: 'discountCode',
      series: [{ key: 'totalAmount', label: 'Monto' }],
    },
  },
  {
    key: 'avg-term-by-branch',
    title: 'Plazos promedio',
    description: 'Meses promedio de plazo por sucursal',
    group: 'Ventas — Saldos',
    needsDates: true,
    chart: {
      type: 'bar',
      labelKey: 'branchName',
      series: [{ key: 'avgTermMonths', label: 'Meses', color: '#7c3aed' }],
    },
  },
  {
    key: 'receivables',
    title: 'Dinero por cobrar',
    description: 'Saldos pendientes por préstamo',
    group: 'Ventas — Saldos',
    needsDates: true,
    statusOptions: [
      { value: '', label: 'Todos' },
      { value: 'OVERDUE', label: 'Vencida' },
      { value: 'PARTIAL', label: 'Parcial' },
      { value: 'PENDING', label: 'Pendiente' },
    ],
    chart: {
      type: 'bar',
      labelKey: 'branchName',
      series: [{ key: 'amountDue', label: 'Saldo', color: '#d97706' }],
    },
  },
  {
    key: 'penalties-collected',
    title: 'Punitorios cobrados',
    description: 'Intereses punitorios cobrados por sucursal',
    group: 'Ventas — Saldos',
    needsDates: true,
    chart: {
      type: 'bar',
      labelKey: 'branchName',
      series: [{ key: 'penaltyCollected', label: 'Punitorios', color: '#dc2626' }],
    },
  },
  {
    key: 'real-collection-by-branch',
    title: 'Cobranza real por sucursal',
    description: 'Pagos en efectivo por sucursal',
    group: 'Ventas — Saldos',
    needsDates: true,
    userFilter: true,
    chart: {
      type: 'bar',
      labelKey: 'branchName',
      series: [{ key: 'cashCollected', label: 'Cobrado', color: '#059669' }],
    },
  },
  {
    key: 'delinquency-list',
    title: 'Listado de morosos',
    description: 'Cuotas vencidas con saldo pendiente',
    group: 'Mora',
    needsDates: true,
    statusOptions: [
      { value: '', label: 'Todos' },
      { value: 'OVERDUE', label: 'Vencida' },
      { value: 'PARTIAL', label: 'Parcial' },
      { value: 'PENDING', label: 'Pendiente' },
    ],
    chart: {
      type: 'bar',
      labelKey: 'branchName',
      series: [{ key: 'outstanding', label: 'Saldo mora', color: '#dc2626' }],
    },
  },
  {
    key: 'delinquency',
    title: 'Informe de morosidad',
    description: 'KPI agregado de cartera vencida',
    group: 'Mora',
    needsDates: true,
    needsDelinquencyType: true,
  },
  {
    key: 'legal-affectations',
    title: 'Afectaciones a juicio',
    description: 'Registros Veraz / bureau',
    group: 'Mora',
    needsDates: true,
    statusOptions: [
      { value: '', label: 'Todos' },
      { value: 'PENDING', label: 'Pendiente' },
      { value: 'REPORTED', label: 'Reportado' },
      { value: 'CANCELLED', label: 'Cancelado' },
    ],
    userFilter: true,
    chart: {
      type: 'pie',
      labelKey: 'status',
      series: [{ key: 'overdueAmount', label: 'Monto' }],
    },
  },
  {
    key: 'sales-by-user',
    title: 'Ventas / cobranza por usuario',
    description: 'Cobranza en caja y ventas convertidas por usuario',
    group: 'Estadísticas',
    needsDates: true,
    chart: {
      type: 'bar',
      labelKey: 'userName',
      series: [
        { key: 'collectionAmount', label: 'Cobranza', color: '#059669' },
        { key: 'salesAmount', label: 'Ventas', color: '#0f172a' },
      ],
    },
  },
  {
    key: 'how-heard',
    title: 'Cómo se enteró',
    description: 'Presolicitudes agrupadas por fuente de referidos',
    group: 'Estadísticas',
    needsDates: true,
    chart: {
      type: 'pie',
      labelKey: 'sourceName',
      series: [{ key: 'count', label: 'Cantidad' }],
    },
  },
  {
    key: 'renewals-by-type',
    title: 'Renovaciones y reingresos',
    description: 'Desembolsos con préstamo padre (renovación / reingreso / refinance)',
    group: 'Estadísticas',
    needsDates: true,
    chart: {
      type: 'bar',
      labelKey: 'branchName',
      series: [
        { key: 'totalAmount', label: 'Monto', color: '#7c3aed' },
        { key: 'loanCount', label: 'Cantidad', color: '#2563eb' },
      ],
    },
  },
  {
    key: 'collection-rate-by-bucket',
    title: '% cobranza por atraso',
    description: 'Tasa de cobro según días de mora al cierre del período',
    group: 'Cobranza',
    needsDates: true,
    chart: {
      type: 'bar',
      labelKey: 'bucket',
      series: [{ key: 'collectionRatePercent', label: '% cobrado', color: '#2563eb' }],
    },
  },
  {
    key: 'calls-by-user',
    title: 'Llamados por usuario',
    description: 'Contactos y notas de llamado en cobranza',
    group: 'Cobranza',
    needsDates: true,
    userFilter: true,
    chart: {
      type: 'bar',
      labelKey: 'userName',
      series: [{ key: 'totalCalls', label: 'Llamados', color: '#0891b2' }],
    },
  },
  {
    key: 'loyalty-collected',
    title: 'Puntos cobrados',
    description: 'Movimientos de fidelización',
    group: 'Cobranza',
    needsDates: true,
    userFilter: true,
  },
  {
    key: 'monthly-financed',
    title: 'Financiado mensual',
    description: 'Desembolsos mes a mes',
    group: 'Cobranza',
    needsYear: true,
    chart: {
      type: 'bar',
      labelKey: 'month',
      series: [{ key: 'totalFinanced', label: 'Financiado', color: '#0f172a' }],
    },
  },
  {
    key: 'cash-audits',
    title: 'Arqueos',
    description: 'Arqueos de caja registrados',
    group: 'Caja y seguros',
    needsDates: true,
    userFilter: true,
  },
  {
    key: 'insurance',
    title: 'Aseguradora',
    description: 'Pólizas vinculadas a préstamos',
    group: 'Caja y seguros',
    needsDates: true,
    statusOptions: [
      { value: '', label: 'Todos' },
      { value: 'ACTIVE', label: 'Activa' },
      { value: 'CANCELLED', label: 'Cancelada' },
    ],
  },
  {
    key: 'loan-applications',
    title: 'Presolicitudes',
    description: 'Solicitudes de crédito',
    group: 'CRM y solicitudes',
    needsDates: true,
    statusOptions: [
      { value: '', label: 'Todos' },
      { value: 'PENDING', label: 'Pendiente' },
      { value: 'APPROVED', label: 'Aprobada' },
      { value: 'REJECTED', label: 'Rechazada' },
      { value: 'CONVERTED', label: 'Convertida' },
    ],
    userFilter: true,
  },
  {
    key: 'social-leads',
    title: 'Contacto redes sociales',
    description: 'Leads de redes sociales',
    group: 'CRM y solicitudes',
    needsDates: true,
    statusOptions: [
      { value: '', label: 'Todos' },
      { value: 'NEW', label: 'Nuevo' },
      { value: 'CONTACTED', label: 'Contactado' },
      { value: 'QUALIFIED', label: 'Calificado' },
      { value: 'CONVERTED', label: 'Convertido' },
      { value: 'DISCARDED', label: 'Descartado' },
    ],
    userFilter: true,
  },
];

function defaultDateRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    from: start.toISOString().slice(0, 10),
    to: now.toISOString().slice(0, 10),
  };
}

function normalizeRows(data: unknown, reportKey?: string): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;

    if (reportKey === 'sales-by-user') {
      const collections = Array.isArray(obj.collectionsByUser)
        ? (obj.collectionsByUser as Record<string, unknown>[])
        : [];
      const sales = Array.isArray(obj.loanSalesByUser)
        ? (obj.loanSalesByUser as Record<string, unknown>[])
        : [];
      const byUser = new Map<string, Record<string, unknown>>();
      for (const r of collections) {
        const id = String(r.userId ?? r.userName ?? '');
        byUser.set(id, {
          userId: r.userId,
          userName: r.userName,
          voucherCount: r.voucherCount ?? 0,
          collectionAmount: r.collectionAmount ?? 0,
          loansConverted: 0,
          salesAmount: 0,
        });
      }
      for (const r of sales) {
        const id = String(r.userId ?? r.userName ?? '');
        const cur = byUser.get(id) ?? {
          userId: r.userId,
          userName: r.userName,
          voucherCount: 0,
          collectionAmount: 0,
          loansConverted: 0,
          salesAmount: 0,
        };
        cur.loansConverted = r.loansConverted ?? 0;
        cur.salesAmount = r.salesAmount ?? 0;
        byUser.set(id, cur);
      }
      return Array.from(byUser.values());
    }

    if (reportKey === 'sales-avg-by-branch' && Array.isArray(obj.rows)) {
      return (obj.rows as Record<string, unknown>[]).map((r) => ({
        ...r,
        label: `${String(r.branchName ?? '').slice(0, 18)} · ${String(r.sellerName ?? '—')}`.slice(0, 36),
      }));
    }

    if (reportKey === 'renewals-by-type' && Array.isArray(obj.rows)) {
      return obj.rows as Record<string, unknown>[];
    }

    if (Array.isArray(obj.rows)) return obj.rows as Record<string, unknown>[];
    if (Array.isArray(obj.months)) return obj.months as Record<string, unknown>[];
    if (Array.isArray(obj.byType)) return obj.byType as Record<string, unknown>[];
    return [obj];
  }
  return [];
}

function money(n: number) {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(n);
}

/** Agrega filas por labelKey cuando el reporte viene a nivel préstamo (ej. receivables). */
function chartDataFromRows(
  rows: Record<string, unknown>[],
  chart: ChartConfig,
): Record<string, unknown>[] {
  const { labelKey, series } = chart;
  if (rows.length === 0) return [];

  const hasLabel = rows.some((r) => r[labelKey] != null && String(r[labelKey]).length > 0);
  if (!hasLabel) return rows.slice(0, 20);

  const uniqueLabels = new Set(rows.map((r) => String(r[labelKey] ?? '')));
  if (uniqueLabels.size === rows.length && rows.length <= 25) {
    return rows.map((r) => ({
      ...r,
      [labelKey]: String(r[labelKey] ?? '—').slice(0, 28),
    }));
  }

  const agg = new Map<string, Record<string, unknown>>();
  for (const row of rows) {
    const label = String(row[labelKey] ?? 'Sin sucursal');
    const cur = agg.get(label) ?? { [labelKey]: label.slice(0, 28) };
    for (const s of series) {
      const v = Number(row[s.key] ?? 0);
      cur[s.key] = Number(cur[s.key] ?? 0) + (Number.isFinite(v) ? v : 0);
    }
    agg.set(label, cur);
  }
  return Array.from(agg.values());
}

function ReportChart({
  chart,
  rows,
}: {
  chart: ChartConfig;
  rows: Record<string, unknown>[];
}) {
  const data = useMemo(() => chartDataFromRows(rows, chart), [rows, chart]);
  if (data.length === 0) return null;

  const primaryKey = chart.series[0]?.key;
  if (chart.type === 'pie' && primaryKey) {
    return (
      <div className="h-72 mb-4 border rounded-lg p-3 bg-slate-50/50">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey={primaryKey}
              nameKey={chart.labelKey}
              cx="50%"
              cy="50%"
              outerRadius={100}
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number) => money(Number(value))}
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="h-72 mb-4 border rounded-lg p-3 bg-slate-50/50">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey={chart.labelKey} tick={{ fontSize: 11 }} stroke="#94a3b8" interval={0} angle={-20} textAnchor="end" height={60} />
          <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
          <Tooltip
            contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
            formatter={(value: number, name: string) => {
              const series = chart.series.find((s) => s.key === name || s.label === name);
              const label = series?.label ?? name;
              const isMoney = /amount|collected|due|penalty|financiado|total/i.test(String(name))
                || /Monto|Promedio|Saldo|Cobrado|Punitorios|Financiado/i.test(label);
              return [isMoney ? money(Number(value)) : value, label];
            }}
          />
          <Legend />
          {chart.series.map((s) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              fill={s.color ?? CHART_COLORS[0]}
              radius={[4, 4, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function LegacyReports() {
  const range = defaultDateRange();
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const [year, setYear] = useState(new Date().getFullYear());
  const [selectedBranchIds, setSelectedBranchIds] = useState<string[]>([]);
  const [status, setStatus] = useState('');
  const [userId, setUserId] = useState('');
  const [delinquencyType, setDelinquencyType] = useState('EMPRESA');
  const [selectedKey, setSelectedKey] = useState('sales-by-branch');
  const [branches, setBranches] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'csv' | 'xlsx' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<Record<string, unknown>[]>([]);
  const [summary, setSummary] = useState<Record<string, unknown> | null>(null);

  const reportDef = useMemo(
    () => LEGACY_REPORTS.find((r) => r.key === selectedKey) ?? LEGACY_REPORTS[0],
    [selectedKey],
  );

  const groupedReports = useMemo(() => {
    return REPORT_GROUPS.map((group) => ({
      group,
      reports: LEGACY_REPORTS.filter((r) => r.group === group),
    })).filter((g) => g.reports.length > 0);
  }, []);

  useEffect(() => {
    Promise.all([apiService.getBranches(true), apiService.listStaffUsers()])
      .then(([b, u]) => {
        setBranches(b);
        setUsers(u);
      })
      .catch(() => {});
  }, []);

  const toggleBranch = (id: string) => {
    setSelectedBranchIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const selectAllBranches = () => setSelectedBranchIds([]);

  const buildParams = () => {
    const p: Record<string, string | number | undefined> = {};
    if (reportDef.needsDates) {
      p.from = from;
      p.to = to;
    }
    if (reportDef.needsYear) p.year = year;
    if (reportDef.needsDelinquencyType) p.type = delinquencyType;
    if (selectedBranchIds.length > 0) p.branchId = selectedBranchIds.join(',');
    if (status) p.status = status;
    if (userId) p.userId = userId;
    return p;
  };

  const loadReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getLegacyReport(selectedKey, buildParams());
      setPreview(normalizeRows(data, selectedKey));
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const { rows, months, collectionsByUser, loanSalesByUser, byType, branchTotals, ...rest } =
          data as Record<string, unknown>;
        const summaryBits: Record<string, unknown> = { ...rest };
        if (Array.isArray(branchTotals) && branchTotals.length) {
          summaryBits.sucursales = branchTotals.length;
          summaryBits.promedioSucursales = (
            branchTotals as Array<{ branchName?: string; avgAmount?: number }>
          )
            .map((b) => `${b.branchName}: ${b.avgAmount}`)
            .slice(0, 6)
            .join(' · ');
        }
        if (Object.keys(summaryBits).length > 0) setSummary(summaryBits);
        else setSummary(null);
      } else {
        setSummary(null);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
      setPreview([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format: 'csv' | 'xlsx') => {
    setExporting(format);
    setError(null);
    try {
      const params = { report: selectedKey, ...buildParams() };
      if (format === 'csv') await apiService.downloadLegacyReportCsv(params);
      else await apiService.downloadOperationalReportXlsx(params);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setExporting(null);
    }
  };

  const columns = preview.length > 0 ? Object.keys(preview[0]) : [];
  const branchFilterLabel =
    selectedBranchIds.length === 0
      ? 'Todas'
      : selectedBranchIds.length === 1
        ? branches.find((b) => b.id === selectedBranchIds[0])?.name ?? '1 sucursal'
        : `${selectedBranchIds.length} sucursales`;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Reportes legacy</h1>
        <p className="text-sm text-gray-500 mt-1">
          Listados del sistema anterior — fechas, varias sucursales y gráficos
        </p>
      </div>

      {error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <aside className="lg:col-span-1 bg-white border rounded-lg p-3 max-h-[75vh] overflow-y-auto">
          {groupedReports.map(({ group, reports }) => (
            <div key={group} className="mb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase px-2 mb-1 tracking-wide">
                {group}
              </p>
              {reports.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => {
                    setSelectedKey(r.key);
                    setPreview([]);
                    setSummary(null);
                    setStatus('');
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm mb-0.5 ${
                    selectedKey === r.key
                      ? 'bg-blue-50 text-blue-800 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {r.title}
                </button>
              ))}
            </div>
          ))}
        </aside>

        <div className="lg:col-span-3 space-y-4">
          <div className="bg-white border rounded-lg p-4">
            <h2 className="text-lg font-semibold">{reportDef.title}</h2>
            <p className="text-sm text-gray-500 mb-4">{reportDef.description}</p>

            <div className="flex flex-wrap gap-3 items-end mb-3">
              {reportDef.needsDates && (
                <>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Desde</label>
                    <input
                      type="date"
                      value={from}
                      onChange={(e) => setFrom(e.target.value)}
                      className="px-3 py-2 border rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Hasta</label>
                    <input
                      type="date"
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                      className="px-3 py-2 border rounded-md text-sm"
                    />
                  </div>
                </>
              )}
              {reportDef.needsYear && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Año</label>
                  <input
                    type="number"
                    value={year}
                    onChange={(e) => setYear(parseInt(e.target.value, 10))}
                    className="px-3 py-2 border rounded-md text-sm w-24"
                  />
                </div>
              )}
              {reportDef.statusOptions && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Estado</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="px-3 py-2 border rounded-md text-sm"
                  >
                    {reportDef.statusOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {reportDef.userFilter && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Usuario</label>
                  <select
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    className="px-3 py-2 border rounded-md text-sm min-w-[160px]"
                  >
                    <option value="">Todos</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.firstName} {u.lastName}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {reportDef.needsDelinquencyType && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Tipo morosidad</label>
                  <select
                    value={delinquencyType}
                    onChange={(e) => setDelinquencyType(e.target.value)}
                    className="px-3 py-2 border rounded-md text-sm"
                  >
                    {DELINQUENCY_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <button
                type="button"
                onClick={loadReport}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Generando…' : 'Generar'}
              </button>
              <button
                type="button"
                onClick={() => exportReport('csv')}
                disabled={!!exporting}
                className="px-3 py-2 border rounded-md text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                {exporting === 'csv' ? '…' : 'CSV'}
              </button>
              <button
                type="button"
                onClick={() => exportReport('xlsx')}
                disabled={!!exporting}
                className="px-3 py-2 border rounded-md text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                {exporting === 'xlsx' ? '…' : 'Excel'}
              </button>
            </div>

            <div className="mb-4 p-3 border rounded-md bg-gray-50">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <label className="text-xs font-medium text-gray-700">
                  Sucursales <span className="font-normal text-gray-500">({branchFilterLabel})</span>
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={selectAllBranches}
                    className="text-xs text-blue-700 hover:underline"
                  >
                    Todas
                  </button>
                  {selectedBranchIds.length > 0 && (
                    <button
                      type="button"
                      onClick={selectAllBranches}
                      className="text-xs text-gray-500 hover:underline"
                    >
                      Limpiar filtro
                    </button>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-2 max-h-28 overflow-y-auto">
                {branches.map((b) => {
                  const checked =
                    selectedBranchIds.length === 0 || selectedBranchIds.includes(b.id);
                  const indeterminate = selectedBranchIds.length === 0;
                  return (
                    <label key={b.id} className="inline-flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        ref={(el) => {
                          if (el) el.indeterminate = indeterminate;
                        }}
                        onChange={() => {
                          if (selectedBranchIds.length === 0) {
                            setSelectedBranchIds([b.id]);
                          } else {
                            toggleBranch(b.id);
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                      {b.name}
                    </label>
                  );
                })}
              </div>
              <p className="text-[11px] text-gray-500 mt-2">
                Vacío / “Todas” = sucursales permitidas. Podés marcar varias a la vez.
              </p>
            </div>

            {summary && (
              <div className="mb-4 p-3 bg-gray-50 rounded text-sm flex flex-wrap gap-4">
                {Object.entries(summary).map(([k, v]) => (
                  <div key={k}>
                    <span className="text-gray-500">{k}: </span>
                    <span className="font-medium">{String(v)}</span>
                  </div>
                ))}
              </div>
            )}

            {preview.length > 0 && reportDef.chart && (
              <ReportChart chart={reportDef.chart} rows={preview} />
            )}

            {preview.length > 0 ? (
              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      {columns.map((c) => (
                        <th
                          key={c}
                          className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap"
                        >
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {preview.slice(0, 100).map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        {columns.map((c) => (
                          <td key={c} className="px-3 py-2 whitespace-nowrap">
                            {String(row[c] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.length > 100 && (
                  <p className="text-xs text-gray-500 p-2">
                    Mostrando 100 de {preview.length} filas. Exportá para ver el listado completo.
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Seleccioná filtros y presioná Generar para ver el listado.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
